import { FinanceApiService, OpenAPI } from 'api/openapi';
import Cookies from 'js-cookie';

// Add a refresh token promise to track ongoing refresh operations
let refreshTokenPromise: Promise<string> | null = null;

export const getAccessToken = async (): Promise<string> => {
  let token = Cookies.get('access_token');
  let expiryTime = Cookies.get('tokenExpiry');
  const refreshTokensCookie = Cookies.get('refreshToken');

  // Check if the token is expired
  if (token) {
    if (!isTokenExpired(expiryTime)) {
      return token;
    } else {
      // Token is expired, use the existing refresh operation or create a new one
      if (!refreshTokenPromise) {
        refreshTokenPromise = refreshTokenApi(refreshTokensCookie, token);
        // Clear the promise reference once complete (whether success or failure)
        refreshTokenPromise.finally(() => {
          refreshTokenPromise = null;
        });
      }
      // Wait for the ongoing refresh operation
      token = await refreshTokenPromise;
      return token;
    }
  }
  return token || '';
};

// Utility function to check if the token is expired
const isTokenExpired = (expiryTimeValue: string | undefined): boolean => {
  if (!expiryTimeValue) return true;

  try {
    const expiryTime = Number(expiryTimeValue);
    const currentTime = new Date().getTime();
    return expiryTime < currentTime;
  } catch {
    return true;
  }
};

export const setRequestHeaders = async (): Promise<void> => {
  const accessToken = await getAccessToken();
  OpenAPI.HEADERS = {
    Authorization: accessToken ? `Bearer ${accessToken}` : '',
    'Accept-Language': 'en-US',
  };
};

export const setCookieTokens = async (response: any): Promise<string> => {
  Cookies.set('access_token', response.accessToken, {
    secure: true,
    sameSite: 'strict',
  });
  Cookies.set('refreshToken', response.refreshToken, {
    secure: true,
    sameSite: 'strict',
  });
  const expiryTime = new Date();
  expiryTime.setSeconds(expiryTime.getSeconds() + response.expiresIn - 300);
  Cookies.set('tokenExpiry', expiryTime.getTime(), {
    secure: true,
    sameSite: 'strict',
  });
  return response.accessToken;
};

const refreshTokenApi = async (
  refreshToken: string | undefined,
  accessToken: string,
): Promise<string> => {
  if (!refreshToken) {
    // Handle case where refresh token is missing
    if (window.location.pathname !== '/login') {
      Cookies.remove('access_token');
      Cookies.remove('refreshToken');
      Cookies.remove('tokenExpiry');
      Cookies.remove('isChangesDone');
      window.location.href = '/login';
    }
    return '';
  }

  try {
    OpenAPI.HEADERS = {
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
      'Accept-Language': 'en-US',
    };
    const response = await FinanceApiService.postAuthRefresh({
      refreshToken: refreshToken,
    });

    return await setCookieTokens(response);
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Use a more graceful error handling approach
    if (window.location.pathname !== '/login') {
      Cookies.remove('access_token');
      Cookies.remove('refreshToken');
      Cookies.remove('tokenExpiry');
      Cookies.remove('isChangesDone');
      window.location.href = '/login';
    }
    return '';
  }
};
